import React from "react";

import { useGetMedicalPlans } from "BenAdminRenewal/hooks";
import { useGetBenAdminRenewalQuery, useGetDentalPlansQuery, useGetVisionPlansQuery } from "BenAdminRenewal/redux";
import { useGetLifePlansQuery, useGetLongTermDisabilityPlansQuery, useGetShortTermDisabilityPlansQuery } from "EmployerApplication/api";
import { SimpleEmployerBreadcrumbs } from "EmployerDashboard/Components/Breadcrumbs";
import { Link, RouteComponentProps } from "react-router-dom";
import { DentalPlan, MedicalPlan, VisionPlan } from "Utils/types";

import Chrome from "../components/Chrome";
import Layout from "../components/Layout";
import { MedicalPlanCard, DentalPlanCard, VisionPlanCard, LifePlanCard, ShortTermDisabilityPlanCard, LongTermDisabilityPlanCard } from "../components/PlanCard";
import { CardSection } from "Cobalt/Card";
import { TitleSmall } from "Cobalt/Header";
import DashboardHeader from "Common/DashboardHeader";
import { Div } from "Common/HTML";
import Icon from "Common/Icon";
import BodyText from "Theme/BodyText";
import { LinkButton } from "Theme/Button";

type Data = {
  isLoading: boolean;
  isLoadingLifePlans: boolean;
  medicalPlans: MedicalPlan[];
  dentalPlans: DentalPlan[];
  visionPlans: VisionPlan[];
  lifePlans: any[];
  shortTermDisabilityPlans: any[];
  longTermDisabilityPlans: any[];
};
const useData = (): Data => {
  const {
    isLoading: isLoadingBenAdminRenewal,
    data: {
      companyPolicyID,
      companyIDToken,
      medicalPlanIds,
      dentalPlanIds,
      visionPlanIds,
      lifePlanIds,
      shortTermDisabilityPlanIds,
      longTermDisabilityPlanIds,
      zipCode,
    },
  } = useGetBenAdminRenewalQuery(null);
  const { isLoading: isLoadingMedicalPlans, plans: medicalPlans } = useGetMedicalPlans(medicalPlanIds, zipCode, companyIDToken);
  const { isLoading: isLoadingDentalPlans, data: { results: dentalPlans } = { results: [] } } = useGetDentalPlansQuery(companyPolicyID, {
    skip: dentalPlanIds.length === 0,
  });
  const { isLoading: isLoadingVisionPlans, data: { results: visionPlans } = { results: [] } } = useGetVisionPlansQuery(companyPolicyID, {
    skip: visionPlanIds.length === 0,
  });
  const { isFetching: isFetchingLifePlans, data: { results: lifePlans } = { results: [] } } = useGetLifePlansQuery(
    {
      companyIDToken,
      useEmployeeForms: false,
      companyZipCode: zipCode,
    },
    { skip: lifePlanIds.length === 0 },
  );
  const { isLoading: isLoadingShortTermDisabilityPlans, data: { results: shortTermDisabilityPlans } = { results: [] } } = useGetShortTermDisabilityPlansQuery(
    { companyIDToken },
    { skip: shortTermDisabilityPlanIds.length === 0 },
  );
  const { isLoading: isLoadingLongTermDisabilityPlans, data: { results: longTermDisabilityPlans } = { results: [] } } = useGetLongTermDisabilityPlansQuery(
    { companyIDToken },
    { skip: longTermDisabilityPlanIds.length === 0 },
  );

  // Life Plans get fetched from UHC, this can take a while.
  // Overriding the loading state if no life plans are in the renewal.
  const isLoadingLifePlans = lifePlanIds.length > 0 && isFetchingLifePlans;
  return {
    isLoading:
      isLoadingBenAdminRenewal ||
      isLoadingMedicalPlans ||
      isLoadingDentalPlans ||
      isLoadingVisionPlans ||
      isLoadingShortTermDisabilityPlans ||
      isLoadingLongTermDisabilityPlans,
    isLoadingLifePlans,
    medicalPlans,
    dentalPlans: dentalPlans.filter((plan) => dentalPlanIds.includes(plan.id)),
    visionPlans: visionPlans.filter((plan) => visionPlanIds.includes(plan.id)),
    lifePlans: lifePlans.filter((plan) => lifePlanIds.includes(plan.id)),
    shortTermDisabilityPlans: shortTermDisabilityPlans.filter((plan) => shortTermDisabilityPlanIds.includes(plan.id)),
    longTermDisabilityPlans: longTermDisabilityPlans.filter((plan) => longTermDisabilityPlanIds.includes(plan.id)),
  };
};

const OverviewPage: React.FC<RouteComponentProps<{}>> = () => {
  const { isLoading, isLoadingLifePlans, medicalPlans, dentalPlans, visionPlans, lifePlans, longTermDisabilityPlans, shortTermDisabilityPlans } = useData();
  const title = "Renewal Overview";

  return (
    <Chrome title={title}>
      <Layout>
        <SimpleEmployerBreadcrumbs title={title} px={0} />
        <DashboardHeader title={title} subtitle="These are your plans for the upcoming policy" px={0} />
        {isLoading ? (
          <BodyText>Loading...</BodyText>
        ) : (
          <CardSection px={0}>
            <TitleSmall mt={0} mb={0}>
              Medical
            </TitleSmall>
            {medicalPlans.map((plan, index) => (
              <Div mt={4} key={index}>
                <MedicalPlanCard plan={{ ...plan }} />
              </Div>
            ))}
            {dentalPlans.length > 0 && (
              <>
                <TitleSmall mt={5} mb={0}>
                  Dental
                </TitleSmall>
                {dentalPlans.map((plan, index) => (
                  <Div mt={4} key={index}>
                    <DentalPlanCard plan={{ ...plan }} />
                  </Div>
                ))}
              </>
            )}
            {visionPlans.length > 0 && (
              <>
                <TitleSmall mt={5} mb={0}>
                  Vision
                </TitleSmall>
                {visionPlans.map((plan, index) => (
                  <Div mt={4} key={index}>
                    <VisionPlanCard plan={{ ...plan }} />
                  </Div>
                ))}
              </>
            )}
            {isLoadingLifePlans && <BodyText>Loading life plans...</BodyText>}
            {lifePlans.length > 0 && (
              <>
                <TitleSmall mt={5} mb={0}>
                  Life
                </TitleSmall>
                {lifePlans.map((plan, index) => (
                  <Div mt={4} key={index}>
                    <LifePlanCard plan={{ ...plan }} />
                  </Div>
                ))}
              </>
            )}
            {shortTermDisabilityPlans.length > 0 && (
              <>
                <TitleSmall mt={5} mb={0}>
                  Short Term Disability
                </TitleSmall>
                {shortTermDisabilityPlans.map((plan, index) => (
                  <Div mt={4} key={index}>
                    <ShortTermDisabilityPlanCard plan={{ ...plan }} />
                  </Div>
                ))}
              </>
            )}
            {longTermDisabilityPlans.length > 0 && (
              <>
                <TitleSmall mt={5} mb={0}>
                  Long Term Disability
                </TitleSmall>
                {longTermDisabilityPlans.map((plan, index) => (
                  <Div mt={4} key={index}>
                    <LongTermDisabilityPlanCard plan={{ ...plan }} />
                  </Div>
                ))}
              </>
            )}
          </CardSection>
        )}
        <CardSection className="flex justify-between" px={0}>
          <LinkButton component={Link} size="medium" to="/employer-dashboard" color="link" pl={0}>
            <Icon icon="chevron-left" pr={3} />
            Back to Dashboard
          </LinkButton>
          <LinkButton component={Link} to="/benefits-administration/renewal/policy-details" size="medium" disabled={isLoading}>
            Next
          </LinkButton>
        </CardSection>
      </Layout>
    </Chrome>
  );
};

export default OverviewPage;
